import { esql, ComposerQuery, EsqlQuery } from "@elastic/esql"

// All ES|QL composition flows through @elastic/esql. We never construct query strings by hand.

// DOM data-attributes arrive as strings; coerce to native JS types so the composer's parameter
// binding emits the right literal form (number unquoted, boolean keyword, string quoted+escaped).
function coerce(value, typeHint) {
  if (typeHint === "number") return Number(value)
  if (typeHint === "boolean") return String(value) === "true"
  return String(value)
}

// Returns the dotted name of a column AST node, or null if not a recognizable column.
function columnName(node) {
  if (!node || node.type !== "column" || !Array.isArray(node.args)) return null
  return node.args.map((part) => part?.name).filter(Boolean).join(".")
}

// Extracts the JS value from a literal AST node, normalized to match coerce()'s output.
// Returns undefined for unrecognized literal shapes (defensive default — caller treats as
// "no match" so we'd rather over-add a filter than silently swallow a click).
function literalValue(node) {
  if (!node || node.type !== "literal") return undefined
  switch (node.literalType) {
  case "integer":
  case "double":
    return node.value
  case "keyword":
    return node.valueUnquoted
  case "boolean":
    return node.value === "true"
  default:
    return undefined
  }
}

// Recursively true if `node` (or any AND-chained descendant) is `field == expectedValue`.
function containsEquality(node, field, expectedValue) {
  if (!node || node.type !== "function" || node.subtype !== "binary-expression") return false
  const op = node.name?.toLowerCase()

  if (op === "and") {
    return containsEquality(node.args?.[0], field, expectedValue)
        || containsEquality(node.args?.[1], field, expectedValue)
  }

  if (op === "==" && Array.isArray(node.args) && node.args.length === 2) {
    return columnName(node.args[0]) === field && literalValue(node.args[1]) === expectedValue
  }

  return false
}

// True when any WHERE command in the pipeline already contains `field == coerced(value)`.
function hasDuplicatePredicate(ast, field, value, typeHint) {
  if (!ast?.commands) return false
  const expected = coerce(value, typeHint)
  return ast.commands.some(
    (cmd) => cmd.name === "where"
      && cmd.args?.length > 0
      && containsEquality(cmd.args[0], field, expected)
  )
}

// Returns one of:
//   { status: "ok", text }            — textarea should be replaced with `text`
//   { status: "duplicate" }           — predicate already present, no-op
//   { status: "parseError" }          — current text doesn't parse, no-op
function appendFilter(text, field, value, typeHint) {
  const trimmed = (text || "").trim()
  const literal = coerce(value, typeHint)

  if (trimmed === "") {
    const composer = esql`FROM logs`.where(`${field} == ?val`, { val: literal })
    return { status: "ok", text: composer.inlineParams().print("basic") }
  }

  const parsed = EsqlQuery.fromSrc(trimmed)
  if (parsed.errors.length > 0) {
    console.warn("[esql_composition] parse error — skipping filter", parsed.errors)
    return { status: "parseError" }
  }

  if (hasDuplicatePredicate(parsed.ast, field, value, typeHint)) {
    return { status: "duplicate" }
  }

  const composer = new ComposerQuery(parsed.ast).where(`${field} == ?val`, { val: literal })
  return { status: "ok", text: composer.inlineParams().print("basic") }
}

// Normalizes input so semantically-equivalent queries compare equal regardless of whitespace
// or formatting. Falls back to trimmed raw text when the parser rejects the input or the
// composer print path throws — important because this runs during query-composer's connect()
// and an unhandled throw there would leave the controller uninitialized.
function normalizedText(text) {
  if (!text) return ""
  const trimmed = text.trim()
  if (trimmed === "") return ""
  const parsed = EsqlQuery.fromSrc(trimmed)
  if (parsed.errors.length > 0) return trimmed
  try {
    return new ComposerQuery(parsed.ast).inlineParams().print("basic")
  } catch (e) {
    console.warn("[esql_composition] composer print failed in normalizedText", e)
    return trimmed
  }
}

// True when `current` differs semantically from `committed` (the last-submitted query).
// Drives the "Pending filters" badge.
function isDirty(currentText, committedText) {
  return normalizedText(currentText) !== normalizedText(committedText)
}

export { appendFilter, isDirty }
